import React from 'react';
import {
  Box,
  Icons,
  InputBase,
  Loader,
  TextField,
  Typography,
} from '@ublend-npm/aulaui-core';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import StudentRow from './StudentRow';
import {
  ExtensionStudent,
  ManageExtensionStyles,
} from './ManageExtension.types';

type Props = {
  extensionName: string;
  isLoading: boolean;
  onExtensionNameChange: (value: string) => void;
  onOpenCohorts: () => void;
  onSearchChange: (value: string) => void;
  onToggleStudent: (studentId: string) => void;
  search: string;
  searchResults: ExtensionStudent[];
  selectedIds: string[];
  selectedStudents: ExtensionStudent[];
  styles: ManageExtensionStyles;
  unselectedCount: number;
};

const ExtensionStudentsStep = ({
  extensionName,
  isLoading,
  onExtensionNameChange,
  onOpenCohorts,
  onSearchChange,
  onToggleStudent,
  search,
  searchResults,
  selectedIds,
  selectedStudents,
  styles,
  unselectedCount,
}: Props) => {
  const hasSearch = search.trim().length > 0;
  const visibleStudents = hasSearch ? searchResults : selectedStudents;
  const showLoader = isLoading && selectedIds.length > 0;

  return (
    <Box sx={styles.studentsStep}>
      <Typography sx={styles.extensionCount}>Extension info</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <TextField
          autoFocus
          label="Extension name"
          placeholder="Extension name"
          aria-label="Extension name"
          value={extensionName}
          sx={{
            ...styles.extensionNameTextField,
            '& .MuiFormHelperText-root': {
              display: 'none',
            },
            '& .MuiInputBase-root': {
              height: '100%',
            },
          }}
          onChange={(event) => onExtensionNameChange(event.target.value)}
        />
      </Box>
      <Box component="hr" sx={styles.extensionNameSeparator} />
      <Box sx={styles.summaryHeader}>
        <Typography component="h3" sx={styles.sectionTitle}>
          Assign students ({selectedStudents.length})
        </Typography>
        {!!unselectedCount && (
          <Box
            component="button"
            aria-label={`${unselectedCount} in other cohorts`}
            sx={styles.otherCohortsButton}
            onClick={onOpenCohorts}
          >
            {unselectedCount} in other cohorts{' '}
            <Icons.ArrowForward sx={{ height: '18px', width: '18px' }} />
          </Box>
        )}
      </Box>
      <Typography sx={styles.cohortNotice}>
        Students added here will be removed from cohorts
      </Typography>
      <Box sx={styles.chip}>Students</Box>
      <InputBase
        fullWidth
        placeholder="Search for students"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        startAdornment={<Icons.Search sx={styles.searchIcon} />}
        sx={styles.searchInput}
        inputProps={{
          'aria-label': 'Search for students',
          role: 'searchbox',
        }}
      />
      {showLoader && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Loader size={32} />
        </Box>
      )}
      {!isLoading && visibleStudents.length > 0 && (
        <Box component="ul" sx={styles.studentList}>
          {visibleStudents.map((student) => (
            <StudentRow
              key={student.id}
              canAdd={hasSearch}
              isSelected={selectedIds.includes(student.id)}
              onToggle={onToggleStudent}
              student={student}
              styles={styles}
              isStudentStep={true}
            />
          ))}
        </Box>
      )}
      {!isLoading && hasSearch && visibleStudents.length === 0 && (
        <NoResultsCard
          title="No student found!"
          description="Remember to check the spelling."
          sx={styles.emptyStudents}
          titleSx={styles.emptyStudentsTitle}
        />
      )}
    </Box>
  );
};

export default ExtensionStudentsStep;
